
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '@/contexts/LanguageContext';
import DesktopNavigation from './navigation/DesktopNavigation';
import MobileNavigation from './navigation/MobileNavigation';
import Logo from './navigation/Logo';
import { useNavigationUtils } from '@/hooks/useNavigationUtils';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { currentLanguage } = useLanguage();
  const location = useLocation();
  const { isWhyFutureLevelActive, isProductVideosActive, isSocialMediaAdsActive } = useNavigationUtils();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu when route changes
    setIsMenuOpen(false);
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || isMenuOpen
          ? 'bg-gradient-to-r from-[#0f1424] via-[#1A1F2C] to-[#0f1424] backdrop-blur-lg shadow-lg shadow-purple-900/30 py-2' 
          : 'bg-transparent py-4'
      }`}
      role="banner"
    >
      <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between md:items-center">
        <Logo scrolled={scrolled} />

        {/* Desktop Navigation */}
        <DesktopNavigation isServicesActive={isProductVideosActive() || isSocialMediaAdsActive()} />

        <div className="flex items-center space-x-4 md:mt-0">
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          <button 
            onClick={toggleMenu} 
            className="md:hidden text-purple-400 hover:text-purple-300 transition-colors relative group"
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
          >
            <div className="relative">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              <span className="absolute -inset-2 rounded-full bg-purple-600/0 group-hover:bg-purple-600/20 transition-colors duration-300"></span>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <MobileNavigation 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen} 
        isServicesActive={isProductVideosActive() || isSocialMediaAdsActive()} 
      />
    </header>
  );
};

export default Header;
